import { readJwtToken } from "../../../util/decode-jwt";
import { Observable } from "../../../util/observable";
import { UserRoles } from "../driven/enums";
import { ICryptoProvider } from "../driven/providers/cryptoFactory";
import { IlocalStorageProvider } from "../driven/providers/localStorageProvider";
import { ISsoAuthPreFlightRequest } from "../driven/repositories/ssoRepository";

export type IAuthController = ReturnType<typeof AuthControllerFactory>;

export const AuthControllerFactory = ({
  AuthService,
  localStorageProvider,
  cryptoJsProvider,
  AuthObserver,
  circadiaEngineProvider,
  electronEngineProvider,
}: {
  AuthService: IAuthService;
  localStorageProvider: IlocalStorageProvider;
  cryptoJsProvider: (secret: string) => ICryptoProvider;
  AuthObserver: InstanceType<typeof Observable>;
  circadiaEngineProvider: ICircadiaEngineProvider;
  electronEngineProvider: IElectronEngineProvider;
}) => {
  const isCircadiaEngine = circadiaEngineProvider.isWindowCircadia();
  const isElectronEngine = electronEngineProvider.isWindowElectron();

  const getCurrentUser: () => IControllerPromiseResponse<IGetCurrentUserResponse> =
    async () => {
      // Android WebView note:
      // Auth is web-first (SSO/2FA) and tokens flow Web -> Android via AndroidAuth.setAuthTokens().
      // Avoid pulling tokens from Android into web storage here to prevent stale-token overwrites.
      if (localStorageProvider.get("sso") === "true") {
        try {
          const currentUserInfo = await AuthService.getCurrentUserInfo();
          const attributes =
            currentUserInfo?.attributes ?? ({} as Record<string, string>);
          const { sub: userID, email } = attributes;
          const firstName = attributes?.["custom:first_name"] ?? "";
          const lastName = attributes?.["custom:last_name"] ?? "";
          return {
            success: true,
            data: {
              user: {
                userID,
                fullName:
                  lastName || firstName ? `${lastName}, ${firstName}` : "",
                firstName,
                lastName,
                status: true,
                email,
                groups: ["cip_users"] as UserGroup[],
                role: "external_user",
                pccOrgUuid: attributes["custom:pcc_org_uuid"],
                pccEmail: attributes["custom:pcc_email"],
                phoneNumber: attributes["phone_number"],
                title: attributes["custom:m_credentials"],
                isNursePractitioner: false,
                signatureFile:
                  (attributes as Record<string, string>)[
                    "custom:signFileName"
                  ] ?? null,
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (localStorageProvider.get("zpax") === "true") {
        try {
          const currentUserInfo = await AuthService.getCurrentUserInfo();
          const attributes =
            currentUserInfo?.attributes ?? ({} as Record<string, string>);
          const { sub: userID, email } = attributes;
          const firstName = attributes["custom:first_name"];
          const lastName = attributes["custom:last_name"];
          const groups = (attributes as any)["cognito:groups"] as UserGroup[];

          return {
            success: true,
            data: {
              user: {
                userID,
                fullName: `${lastName}, ${firstName}`,
                firstName,
                lastName,
                status: true,
                email,
                groups: groups || ["cip_users"],
                role: "external_user",
                title: attributes["custom:m_credentials"],
                isNursePractitioner: false,
                signatureFile:
                  (attributes as Record<string, string>)[
                    "custom:signFileName"
                  ] ?? null,
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (isElectronEngine) {
        try {
          const currentSession = await AuthService.getCurrentSessionPayload();
          const currentUserInfo = await AuthService.getCurrentUserInfo();

          const attributes = currentUserInfo?.attributes;
          const { sub: userID, email } =
            attributes ?? ({} as Record<string, string>);
          const firstName = attributes?.["custom:first_name"] ?? "";
          const lastName = attributes?.["custom:last_name"] ?? "";
          const idToken = currentSession;
          const groups = idToken["cognito:groups"] as UserGroup[];
          const npi = attributes?.["custom:m_npi"] ?? ("" as string);
          const homeHealthId = (attributes as any)[
            "custom:home_health_id"
          ] as string;
          let role = "";
          let isNursePractitioner = false;
          if (groups) {
            groups.forEach((group) => {
              if (group === "MedicalGroup-BillingPractitioner") {
                role = "Billing Practitioner";
                isNursePractitioner = true;
              }
              if (group === "MedicalGroup-Administrator") {
                role = "Administrator";
              }
              if (group === "AdmissionTeam") {
                role = "Admission Team";
              }
              if (group === "VRN") {
                role = "VRN";
              }
              if (group === "AccountManager") {
                role = "Account Manager";
              }
              if (group === "home_health_patient") {
                role = "home_health_patient";
              }
            });
          }
          if (role === "VRN" && npi) {
            role = "Nurse Practitioner";
            isNursePractitioner = true;
          }
          return {
            success: true,
            data: {
              user: {
                groups,
                userID,
                fullName: `${lastName}, ${firstName}`,
                firstName,
                lastName,
                status: true,
                email,
                role,
                title: attributes?.["custom:m_credentials"] ?? "",
                npi: npi || null,
                isNursePractitioner,
                homeHealthId,
                signatureFile:
                  (attributes as Record<string, string> | undefined)?.[
                    "custom:signFileName"
                  ] ?? null,
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (
        isCircadiaEngine &&
        circadiaEngineProvider.intfv >= 5 &&
        circadiaEngineProvider.isIOS
      ) {
        try {
          const jwtToken:
            | {
                IdToken: string;
                AccessToken: string;
              }
            | undefined = await new Promise((resolve, reject) => {
            const observerEvent = ({
              type,
              payload,
            }: {
              type: string;
              payload: {
                success: boolean;
                error?: string;
              };
            }) => {
              AuthObserver.unsubscribe(
                observerEvent as (value: unknown) => void
              );
              if (type === "login") {
                if (!payload.success) {
                  reject(payload.error);
                }
              } else if (type === "pinRequired") {
                reject("pinRequired");
              }
            };
            AuthObserver.subscribe(observerEvent as (value: unknown) => void);
            (async () => {
              await circadiaEngineProvider
                .cognitoTokens()
                .then((tokens: { IdToken: string; AccessToken: string }) => {
                  resolve(tokens);
                });
            })();
          });
          const currentUserInfo = readJwtToken(
            jwtToken ? jwtToken.IdToken : ""
          ) as {
            "custom:first_name": string;
            "custom:last_name": string;
            "cognito:groups": UserGroup[];
            sub: string;
            email: string;
            "custom:home_health_id": string;
            "custom:m_npi": string;
            "custom:m_credentials": string;
          };

          if (currentUserInfo) {
            // const currentUserInfo = await AuthService.getCurrentUserInfo();
            // const attributes = currentUserInfo?.attributes!!;
            const { sub: userID, email } = currentUserInfo;
            const firstName = currentUserInfo["custom:first_name"];
            const lastName = currentUserInfo["custom:last_name"];
            const groups = currentUserInfo["cognito:groups"] as UserGroup[];
            const npi = currentUserInfo["custom:m_npi"] as string;
            const homeHealthId = currentUserInfo[
              "custom:home_health_id"
            ] as string;
            let role = "";
            let isNursePractitioner = false;
            if (groups) {
              groups.forEach((group) => {
                if (group === "MedicalGroup-BillingPractitioner") {
                  role = "Billing Practitioner";
                  isNursePractitioner = true;
                }
                if (group === "MedicalGroup-Administrator") {
                  role = "Administrator";
                }
                if (group === "AdmissionTeam") {
                  role = "Admission Team";
                }
                if (group === "VRN") {
                  role = "VRN";
                }
                if (group === "AccountManager") {
                  role = "Account Manager";
                }
              });
            }

            if (role === "VRN" && npi) {
              role = "Nurse Practitioner";
              isNursePractitioner = true;
            }

            return {
              success: true,
              data: {
                user: {
                  groups,
                  userID,
                  fullName: `${lastName}, ${firstName}`,
                  firstName,
                  lastName,
                  status: true,
                  email,
                  role,
                  title: currentUserInfo["custom:m_credentials"],
                  npi: npi || null,
                  isNursePractitioner,
                  homeHealthId,
                  signatureFile:
                    (currentUserInfo as unknown as Record<string, string>)[
                      "custom:signFileName"
                    ] ?? null,
                },
              },
            };
          }
          throw new Error("No current user info");
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      } else {
        try {
          const currentSession = await AuthService.getCurrentSessionPayload();
          const currentUserInfo = await AuthService.getCurrentUserInfo();

          const attributes = currentUserInfo?.attributes;
          const { sub: userID, email } =
            attributes ?? ({} as Record<string, string>);
          const firstName = attributes?.["custom:first_name"] ?? "";
          const lastName = attributes?.["custom:last_name"] ?? "";
          const groups = currentSession["cognito:groups"] as UserGroup[];
          const npi = attributes?.["custom:m_npi"] ?? ("" as string);
          const homeHealthId = (attributes as any)[
            "custom:home_health_id"
          ] as string;
          let role = "";
          let isNursePractitioner = false;
          if (groups) {
            groups.forEach((group) => {
              if (group === "MedicalGroup-BillingPractitioner") {
                role = "Billing Practitioner";
                isNursePractitioner = true;
              }
              if (group === "MedicalGroup-Administrator") {
                role = "Administrator";
              }
              if (group === "AdmissionTeam") {
                role = "Admission Team";
              }
              if (group === "VRN") {
                role = "VRN";
              }
              if (group === "AccountManager") {
                role = "Account Manager";
              }
            });
          }
          if (role === "VRN" && npi) {
            role = "Nurse Practitioner";
            isNursePractitioner = true;
          }
          return {
            success: true,
            data: {
              user: {
                groups,
                userID,
                fullName: `${lastName}, ${firstName}`,
                firstName,
                lastName,
                status: true,
                email,
                role,
                title: attributes?.["custom:m_credentials"] ?? "",
                npi: npi || null,
                isNursePractitioner,
                homeHealthId,
                signatureFile:
                  (attributes as Record<string, string> | undefined)?.[
                    "custom:signFileName"
                  ] ?? null,
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
    };

  const createPin = async ({
    user,
    key,
    value,
  }: {
    user: User;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const response = await AuthService.createSSPin({ pin: value });

        if (typeof response === "object" && response.status === "error") {
          return { success: false, error: response.message };
        }

        localStorageProvider.set("userHasPinSetup", "true");
        localStorageProvider.set(
          "internalUse",
          JSON.stringify({ email: user.email, keyToCheck: key })
        );
        return {
          success: true,
        };
      } else {
        const { email } = user;

        await AuthService.createSSPin({ pin: value });
        await AuthService.saveSSValue({
          email,
          key,
          value,
        });

        localStorageProvider.set("userHasPinSetup", "true");
        localStorageProvider.set(
          "internalUse",
          JSON.stringify({ email: user.email, keyToCheck: key })
        );
        return {
          success: true,
        };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  };

  const verifyPin = async ({
    email,
    key,
    value,
  }: {
    email: string;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const response = await electronEngineProvider.verifyPin(value);
        if (response.status === "error") {
          return { success: false, error: response.message };
        }
        return {
          success: true,
        };
      }

      if (
        isCircadiaEngine &&
        circadiaEngineProvider.intfv >= 5 &&
        circadiaEngineProvider.isIOS
      ) {
        await AuthService.cognitoProvidePin(value);
      }
      const response = await AuthService.loadSSValue({
        email,
        key,
        value,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: (error as { message: string })?.message,
      };
    }
  };

  const verifyBio = async ({ email, key }: { email: string; key: string }) => {
    try {
      const response = await AuthService.loadSSValueBio({
        email,
        key,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const getUserPass = async ({
    email,
    key,
    value,
  }: {
    email: string;
    key: string;
    value: string;
  }) => {
    try {
      if (
        isCircadiaEngine &&
        circadiaEngineProvider.intfv >= 5 &&
        circadiaEngineProvider.isIOS
      ) {
        await AuthService.cognitoProvidePin(value);
      }
      const response = await AuthService.loadSSValue({
        email,
        key,
        value,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const changePin = async ({
    user,
    key,
    value,
  }: {
    user: User;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        const response = await electronEngineProvider.changePin({
          pin: value,
          token: tokens.idToken,
        });

        if (response.status === "error") {
          return { success: false, error: response.message };
        }
        return {
          success: true,
        };
      }

      const { email } = user;
      await AuthService.changeSSPin({ pin: value });
      const response = await AuthService.saveSSValue({
        email,
        key,
        value,
      });
      return {
        success: true,
        data: response,
      };
    } catch {
      return {
        success: false,
        error: { message: "Error Changing Pin" },
      };
    }
  };

  const login: ILogin = async (payload) => {
    try {
      const user = await AuthService.login(payload);
      const returnData = {
        user: user!,
        challenge: "",
      };
      if (user?.challengeName) {
        returnData.challenge = user.challengeName;
      }

      return {
        success: true,
        data: returnData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? { message: error as string }
            : (error as ICognitoErrorType),
      };
    }
  };

  const ssoLogin: ILogin = async (payload) => {
    const { token, expires_at } = payload;

    if (!token || typeof expires_at !== "number") {
      return {
        success: false,
        error: { message: "Missing SSO token information" },
      };
    }

    try {
      const user = await AuthService.ssoLogin({
        token,
        expires_at,
      });
      const returnData = {
        user: user!,
        challenge: "",
      };

      return {
        success: true,
        data: returnData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
            ? { message: error as string }
            : (error as ICognitoErrorType),
      };
    }
  };

  const checkPassword: ICheckPasswordController = async (payload) => {
    if (electronEngineProvider.isWindowElectron()) {
      const response = await AuthService.checkPassword({
        oldPassword: payload.password,
      });

      if (response.status === "success") {
        return {
          success: true,
          data: {
            user: response.data!,
            challenge: "",
          },
        };
      } else {
        return {
          success: false,
          error: { message: response.message },
        };
      }
    } else {
      return login(payload);
    }
  };

  const forgotPassword: IForgotPassword = async (payload) => {
    try {
      const response = await AuthService.forgotPassword(payload);
      if (response?.status === "error") {
        return {
          success: false,
          error: { message: response.message },
        };
      }
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const resetPassword: IResetPassword = async (payload) => {
    try {
      const res = await AuthService.resetPassword(payload);
      if (res && typeof res !== "string") {
        if (res.status === "error") {
          return { success: false, error: { message: res.message } };
        } else {
          return {
            success: true,
          };
        }
      }
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const completeSMSMFAChallenge: ICompleteSMSMFAChallenge = async (payload) => {
    try {
      const data = await AuthService.completeSMSMFAChallenge(payload);
      return {
        success: true,
        data: {
          user: data!,
          challenge: "",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const completeNewPasswordChallenge: ICompleteNewPasswordChallenge = async (
    payload
  ) => {
    try {
      const data = await AuthService.completeNewPasswordChallenge(payload);
      return {
        success: true,
        data: data!,
      };
    } catch (error) {
      return { success: false, error: error as ICognitoErrorType };
    }
  };

  const logoutUser = async () => {
    try {
      await AuthService.logout();
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const changePassword = async ({
    oldPassword,
    newPassword,
  }: IChangePassword) => {
    try {
      if (isElectronEngine) {
        const data = await AuthService.changePassword({
          oldPassword,
          newPassword,
        });
        return {
          success: true,
          data,
        };
      } else {
        let user: User | string;
        if (
          isCircadiaEngine &&
          circadiaEngineProvider.intfv >= 5 &&
          circadiaEngineProvider.isIOS
        ) {
          user = (await AuthService.getAuthenticationAccessToken()) as string;
        } else {
          user = (await AuthService.currentAuthenticatedUser()) as User;
        }
        if (!user) {
          throw new Error("No current Authenticated User");
        }
        const data = await AuthService.changePassword({
          user,
          oldPassword,
          newPassword,
        });
        return {
          success: true,
          data,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
        data: null,
      };
    }
  };

  const saveEncryptedUserPass = ({ userPass, secretKey }: IEncrypted) => {
    const { encrypted } = cryptoJsProvider(secretKey);
    if (userPass) {
      const encryptedUserPass = cryptoJsProvider(secretKey).encrypt(userPass);
      localStorageProvider.set(encrypted.userPass, encryptedUserPass);
    }
  };

  const getDecryptedUserPass = (secretKey: string): string | null => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedUserPass = localStorageProvider.get(encrypted.userPass);
    if (!encryptedUserPass) {
      return null;
    }
    const decryptedUserPass =
      cryptoJsProvider(secretKey).decrypt(encryptedUserPass);
    return decryptedUserPass;
  };

  const saveEncryptedCredentials = ({ credential, secretKey }: IEncrypted) => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedCredentials =
      cryptoJsProvider(secretKey).encrypt(credential);
    localStorageProvider.set(encrypted.credentials, encryptedCredentials);
  };

  const getDecryptedCredentials = (secretKey: string): string | null => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedCredentials = localStorageProvider.get(
      encrypted.credentials
    );
    if (!encryptedCredentials) {
      return null;
    }
    const decryptedCredentials =
      cryptoJsProvider(secretKey).decrypt(encryptedCredentials);
    return decryptedCredentials;
  };

  const authPreFlight = async (payload: ISsoAuthPreFlightRequest) => {
    try {
      const response = await AuthService.authPreFlight(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return { success: false, error: error as ICognitoErrorType };
    }
  };

  return {
    changePassword,
    completeNewPasswordChallenge,
    completeSMSMFAChallenge,
    getAuthenticationToken: AuthService.getAuthenticationToken,
    getAuthenticationAccessToken: AuthService.getAuthenticationAccessToken,
    getCurrentSessionPayload: AuthService.getCurrentSessionPayload,
    getCurrentUserInfo: AuthService.getCurrentUserInfo,
    getCurrentUser,
    login,
    ssoLogin,
    logoutUser,
    isBP: (role: string) => role === UserRoles.BP,
    isBA: (role: string) => role === UserRoles.BA,
    isAT: (role: string) => role === UserRoles.AT,
    isVRN: (role: string) => role === UserRoles.VRN,
    isAM: (role: string) => role === UserRoles.AM,
    isRCM: (role: string) => role === UserRoles.RCM,
    getDecryptedCredentials,
    saveEncryptedCredentials,
    saveEncryptedUserPass,
    getDecryptedUserPass,
    createPin,
    verifyPin,
    verifyBio,
    changePin,
    getUserPass,
    forgotPassword,
    resetPassword,
    checkPassword,
    authPreFlight,
  };
};
